import { useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { Role } from '@never2busy/shared';
import { usersApi, type User } from '../../../api/endpoints/users';
import { PageHeader } from '../../../components/common/PageHeader';

const createSchema = z.object({
  email: z.string().email('Correo inválido'),
  firstName: z.string().min(1, 'Nombre obligatorio'),
  lastName: z.string().min(1, 'Apellido obligatorio'),
  password: z
    .string()
    .min(10, 'Mínimo 10 caracteres')
    .regex(/[A-Z]/, 'Debe incluir una mayúscula')
    .regex(/[0-9]/, 'Debe incluir un número'),
  role: z.nativeEnum(Role),
  canViewReports: z.boolean().default(true),
});

const editSchema = z.object({
  email: z.string().email('Correo inválido'),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  role: z.nativeEnum(Role),
  canViewReports: z.boolean(),
  active: z.boolean(),
});

type CreateValues = z.infer<typeof createSchema>;
type EditValues = z.infer<typeof editSchema>;

const ROLE_OPTIONS = [
  { value: Role.ADMIN, label: 'Administrador' },
  { value: Role.GERENTE, label: 'Gerente' },
  { value: Role.USUARIO, label: 'Usuario' },
  { value: Role.LECTOR, label: 'Lector' },
];

export function UsersAdminPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);

  const { data, isFetching } = useQuery({
    queryKey: ['users', { page, pageSize }],
    queryFn: () => usersApi.list({ page, pageSize }),
  });

  const removeMutation = useMutation({
    mutationFn: (id: number) => usersApi.remove(id),
    onSuccess: () => {
      message.success('Usuario eliminado');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => message.error('No se pudo eliminar el usuario'),
  });

  const columns: ColumnsType<User> = [
    { title: 'Correo', dataIndex: 'email' },
    {
      title: 'Nombre',
      render: (_, row) => `${row.firstName} ${row.lastName}`,
    },
    {
      title: 'Rol',
      dataIndex: 'role',
      render: (r: Role) => <Tag>{r}</Tag>,
    },
    {
      title: 'Activo',
      dataIndex: 'active',
      render: (a: boolean) =>
        a ? <Tag color="green">Sí</Tag> : <Tag color="red">No</Tag>,
    },
    {
      title: 'Último login',
      dataIndex: 'lastLoginAt',
      render: (d: string | null) =>
        d ? dayjs(d).format('DD/MM/YYYY HH:mm') : 'Nunca',
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'right',
      render: (_, row) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(row)}>
            Editar
          </Button>
          <Popconfirm
            title={`¿Eliminar a ${row.firstName} ${row.lastName}?`}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
            onConfirm={() => removeMutation.mutate(row.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Usuarios"
        crumbs={[{ title: 'Inicio', to: '/' }, { title: 'Admin' }, { title: 'Usuarios' }]}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Nuevo usuario
          </Button>
        }
      />

      <Table<User>
        scroll={{ x: 'max-content' }}
        rowKey="id"
        loading={isFetching}
        columns={columns}
        dataSource={data?.data ?? []}
        pagination={{
          current: page,
          pageSize,
          total: data?.meta.total ?? 0,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
      />

      <CreateUserModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <EditUserModal user={editing} onClose={() => setEditing(null)} />
    </>
  );
}

function CreateUserModal({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      role: Role.USUARIO,
      canViewReports: true,
    },
  });

  const mutation = useMutation({
    mutationFn: (v: CreateValues) => usersApi.create(v),
    onSuccess: () => {
      message.success('Usuario creado');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      reset();
      onClose();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'No se pudo crear';
      message.error(msg);
    },
  });

  return (
    <Modal
      title="Nuevo usuario"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form layout="vertical" onFinish={handleSubmit((v) => mutation.mutate(v))}>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Correo"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input {...field} autoComplete="off" />
            </Form.Item>
          )}
        />
        <Controller
          name="firstName"
          control={control}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Nombre"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input {...field} />
            </Form.Item>
          )}
        />
        <Controller
          name="lastName"
          control={control}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Apellidos"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input {...field} />
            </Form.Item>
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field, fieldState }) => (
            <Form.Item
              label="Contraseña"
              required
              validateStatus={fieldState.error ? 'error' : ''}
              help={fieldState.error?.message}
            >
              <Input.Password {...field} autoComplete="new-password" />
            </Form.Item>
          )}
        />
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <Form.Item label="Rol">
              <Select {...field} options={ROLE_OPTIONS} />
            </Form.Item>
          )}
        />
        <Controller
          name="canViewReports"
          control={control}
          render={({ field }) => (
            <Form.Item label="Puede ver reportes">
              <Switch checked={field.value} onChange={field.onChange} />
            </Form.Item>
          )}
        />
        <Space>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            Crear
          </Button>
          <Button onClick={onClose}>Cancelar</Button>
        </Space>
      </Form>
    </Modal>
  );
}

function EditUserModal({ user, onClose }: { user: User | null; onClose: () => void }): JSX.Element {
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset } = useForm<EditValues>({
    resolver: zodResolver(editSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      role: Role.USUARIO,
      canViewReports: true,
      active: true,
    },
  });

  // Reset cuando cambia el usuario seleccionado
  if (user && user.email !== undefined) {
    // simple sync via setTimeout para evitar setState in render
    setTimeout(() => {
      reset({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.role,
        canViewReports: user.canViewReports,
        active: user.active,
      });
    }, 0);
  }

  const mutation = useMutation({
    mutationFn: (v: EditValues) => usersApi.update(user!.id, v),
    onSuccess: () => {
      message.success('Usuario actualizado');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: () => message.error('No se pudo actualizar'),
  });

  return (
    <Modal
      title={user ? `Editar usuario · ${user.email}` : 'Editar usuario'}
      open={user !== null}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <Form layout="vertical" onFinish={handleSubmit((v) => mutation.mutate(v))}>
        <Controller
          name="email"
          control={control}
          render={({ field, fieldState }) => (
            <Form.Item label="Correo" required validateStatus={fieldState.error ? 'error' : ''}>
              <Input {...field} />
            </Form.Item>
          )}
        />
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <Form.Item label="Nombre" required>
              <Input {...field} />
            </Form.Item>
          )}
        />
        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <Form.Item label="Apellidos" required>
              <Input {...field} />
            </Form.Item>
          )}
        />
        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <Form.Item label="Rol">
              <Select {...field} options={ROLE_OPTIONS} />
            </Form.Item>
          )}
        />
        <Controller
          name="canViewReports"
          control={control}
          render={({ field }) => (
            <Form.Item label="Puede ver reportes">
              <Switch checked={field.value} onChange={field.onChange} />
            </Form.Item>
          )}
        />
        <Controller
          name="active"
          control={control}
          render={({ field }) => (
            <Form.Item label="Activo">
              <Switch checked={field.value} onChange={field.onChange} />
            </Form.Item>
          )}
        />
        <Space>
          <Button type="primary" htmlType="submit" loading={mutation.isPending}>
            Guardar
          </Button>
          <Button onClick={onClose}>Cancelar</Button>
        </Space>
      </Form>
    </Modal>
  );
}
